import React, { useState } from "react";
import { motion } from 'framer-motion';
import axios from 'axios';
import { ASSETS_URL, CREDIT_API_URL } from "../../constants";
import useChatStore from '../../store/useChatStore';
import OtpVerificationModal from '../../utility/OtpVerificationModal';
import showToast from '../../utility/showToast';
import { fadeInUp, buttonVariants } from '../../utility/framerMotionAnimations';

const AnchorHeroSection = () => {
    const [formData, setFormData] = useState({
        name: "",
        businessName: "",
        mobile: "",
        gstNumber: "",
        isConsentAccepted: false
    });
    const [isMobileValid, setIsMobileValid] = useState(false);
    const [isGstValid, setIsGstValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [leadData, setLeadData] = useState(null);
    const [mobileNumberError, setMobileNumberError] = useState('');
    const [gstNumberError, setGstNumberError] = useState('');

    const setCustomer = useChatStore((state) => state.setCustomer);

    const validateMobileNumber = (mobile) => {
        // Validate 10-digit Indian mobile number
        return /^[6-9]\d{9}$/.test(mobile);
    };

    const validateGstNumber = (gst) => {
        // Validate Indian GST number format: 2 digits state code + 10 chars PAN + 1 digit entity + Z + 1 checksum
        // Example: 24AAEPO0824C2ZO
        return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);
    };

    const handleGstNumberChange = (e) => {
        let value = e.target.value.toUpperCase();
        
        // Remove any characters that aren't alphanumeric
        value = value.replace(/[^A-Z0-9]/g, '');
        
        // Limit to 15 characters (standard GST length)
        if (value.length > 15) {
            value = value.substring(0, 15);
        }
        
        setFormData({
            ...formData,
            gstNumber: value
        });
        
        // Real-time validation
        if (value) {
            if (validateGstNumber(value)) {
                setGstNumberError('');
                setIsGstValid(true);
            } else {
                setGstNumberError('Please enter a valid 15-character GST number');
                setIsGstValid(false);
            }
        } else {
            setGstNumberError('');
            setIsGstValid(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // For name and businessName, only allow alphabets and spaces
        if (name === 'name' || name === 'businessName') {
            // Remove any non-alphabetic characters except spaces
            const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
            setFormData({
                ...formData,
                [name]: filteredValue,
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    const handleMobileNumberChange = (e) => {
        let value = e.target.value;
        
        // Remove any non-digit characters
        value = value.replace(/[^\d]/g, '');
        
        // Limit to 10 characters (10 digits)
        if (value.length > 10) {
            value = value.substring(0, 10);
        }
        
        setFormData({
            ...formData,
            mobile: value
        });
        
        // Real-time validation
        if (value) {
            if (validateMobileNumber(value)) {
                setMobileNumberError('');
                setIsMobileValid(true);
            } else {
                setMobileNumberError('Please enter a valid 10-digit mobile number');
                setIsMobileValid(false);
            }
        } else {
            setMobileNumberError('');
            setIsMobileValid(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            showToast("Please enter your name.", "error");
            return;
        }

        if (!formData.businessName.trim()) {
            showToast("Please enter your business name.", "error");
            return;
        }

        if (!formData.gstNumber || !isGstValid) {
            if (!gstNumberError) {
                setGstNumberError('Please enter a valid 15-character GST number');
            }
            showToast("Please enter a valid GST number.", "error");
            return;
        }

        if (!formData.mobile || !isMobileValid) {
            if (!mobileNumberError) {
                setMobileNumberError('Please enter a valid 10-digit mobile number');
            }
            showToast("Please enter a valid mobile number.", "error");
            return;
        }

        if (!formData.isConsentAccepted) {
            showToast("Please accept the consent for mobile verification.", "error");
            return;
        }

        setIsSubmitting(true);

        try {
            // Register anchor first
            const registerPayload = {
                companyName: formData.businessName,
                gstNumber: formData.gstNumber,
                contactName: formData.name,
                contactMobile: formData.mobile,
                isConsentAccepted: formData.isConsentAccepted
            };
            
            const response = await fetch(`${CREDIT_API_URL}anchor/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(registerPayload),
            });
            const result = await response.json();

            if (response.status === 200 || response.status === 201) {
                setLeadData(result.data);
                // Generate OTP after successful registration
                const otpResponse = await axios.post(`${CREDIT_API_URL}generate-otp`, {
                    mobile: formData.mobile,
                    productType: "Supply Chain Financing",
                });
                if (otpResponse.status === 200) {
                    setShowOtpModal(true);
                } else {
                    throw new Error("OTP generation failed.");
                }
            } else {
                showToast(result.message || "Something went wrong.", "error");
            }
        } catch (error) {
            showToast(error.message || "Something went wrong. Please try again.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOtpSuccess = async () => {
        try {
            if (!leadData) throw new Error("Lead data missing. Please try again.");
            
            await setCustomer(leadData);
            useChatStore.setState({ chatSessionId: leadData.chatSessionId, loanId: leadData.loanRequestId });
            
            showToast("Thank you! We'll get back to you soon.", "success");
            
            // Reset form
            setFormData({ name: "", businessName: "", mobile: "", gstNumber: "", isConsentAccepted: false });
            setIsMobileValid(false);
            setIsGstValid(false);
            setMobileNumberError('');
            setGstNumberError('');
        } catch (err) {
            console.error("[ERROR] in handleOtpSuccess:", err);
            showToast(err.message || "OTP verification failed. Please try again.", "error");
        }
    };

    return (
        <section className="anchor-hero-section">
            <div className="anchor-hero-section-center">
                <div className="anchor-hero-section-center-content">
                    <motion.div 
                        className="anchor-hero-form-container"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                        variants={fadeInUp}
                    >
                    <motion.h1 
                        style={{  color: '#283782', marginBottom: '20px' ,marginTop: '0px'}}
                        variants={fadeInUp}
                    >
                        Get Financing Available for your Suppliers
                    </motion.h1>
                        <form className="anchor-hero-form form-horizontal" onSubmit={handleSubmit}>
                          
                            <div className={`business-input-field-container ${formData.name ? 'has-value' : ''}`}>
                                <img src={`${ASSETS_URL}images/form-name-icon.svg`} alt="" style={{ height: '15px' }} />
                                <input 
                                    type="text" 
                                    className="business-input-field" 
                                    required 
                                    placeholder="" 
                                    name="name" 
                                    value={formData.name}
                                    onChange={handleChange} 
                                    autoComplete="off" 
                                    maxLength={50}
                                    id="anchor-name"
                                />
                                <div className="business-hero-placeholder">Enter your Name</div>
                            </div>
                            <div className={`business-input-field-container ${formData.businessName ? 'has-value' : ''}`}>
                                <img src={`${ASSETS_URL}images/form-business-icon.svg`} alt="" style={{ height: '15px' }} />
                                <input 
                                    type="text" 
                                    className="business-input-field" 
                                    required 
                                    placeholder="" 
                                    name="businessName" 
                                    value={formData.businessName}
                                    onChange={handleChange} 
                                    autoComplete="off" 
                                    maxLength={100}
                                    id="anchor-business-name"
                                />
                                <div className="business-hero-placeholder">Enter your Company Name</div>
                            </div>
                            <div className={`business-input-field-container ${formData.gstNumber ? 'has-value' : ''} ${gstNumberError ? 'business-input-field-error' : ''}`}>
                                <img src={`${ASSETS_URL}images/form-business-icon.svg`} alt="" style={{ height: '15px' }} />
                                <input 
                                    type="text" 
                                    className="business-input-field" 
                                    required 
                                    placeholder="" 
                                    name="gstNumber" 
                                    value={formData.gstNumber}
                                    onChange={handleGstNumberChange} 
                                    autoComplete="off" 
                                    maxLength={15}
                                    id="anchor-gst-number"
                                    style={{ textTransform: 'uppercase' }}
                                />
                                <div className="business-hero-placeholder">Enter your GST Number</div>
                            </div>
                            {gstNumberError && (
                                <div className="business-error-message" id="gst-frm-error" style={{ marginTop: '-12px', marginBottom: '10px', justifySelf: 'center' }}>
                                    {gstNumberError}
                                </div>
                            )}
                            <div className={`business-input-field-container ${formData.mobile ? 'has-value' : ''} ${mobileNumberError ? 'business-input-field-error' : ''}`}>
                                <img src="/images/mobile.svg" alt="India" style={{ height: '15px' }} />
                                <input
                                    type="tel"
                                    placeholder=""
                                    value={formData.mobile}
                                    onChange={handleMobileNumberChange}
                                    className="business-input-field"
                                    required
                                    maxLength="10"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    id="anchor-mobile"
                                />
                                <div className="business-hero-placeholder">Enter your Mobile Number</div>
                            </div>
                            {mobileNumberError && (
                                <div className="business-error-message" id="business-frm-error" style={{ marginTop: '-12px', marginBottom: '10px',justifySelf: 'center' }}>
                                    {mobileNumberError}
                                </div>
                            )}
                            <div className="form-group consent-group" style={{ justifyContent: 'center', marginBottom: '10px' , fontSize: '14px'}}>
                                <input
                                    type="checkbox"
                                    id="anchor-consent"
                                    name="isConsentAccepted"
                                    checked={formData.isConsentAccepted}
                                    onChange={e =>
                                        setFormData(prev => ({
                                        ...prev,
                                        isConsentAccepted: e.target.checked
                                    }))
                                    }
                                    style={{ marginLeft: '0px' , width: '15px', height: '15px'}}
                                />
                                <label htmlFor="anchor-consent" style={{ margin: 0, cursor: 'pointer' , fontSize: '14px'}}>
                                I allow GrowBizh to send an OTP for my mobile number verification.
                                </label>
                            </div>
                            <motion.div 
                                className="form-group mb-1" 
                                style={{ justifyContent: 'center', display: 'flex' }}
                                variants={buttonVariants}
                            >
                                <motion.button 
                                    className="business-start-application-btn" 
                                    type="submit"
                                    disabled={
                                        isSubmitting || 
                                        !formData.name.trim() || 
                                        !formData.businessName.trim() || 
                                        !formData.gstNumber.trim() ||
                                        !isGstValid ||
                                        !formData.mobile.trim() || 
                                        !isMobileValid || 
                                        !formData.isConsentAccepted
                                    }
                                    variants={buttonVariants}
                                    whileHover="hover"
                                    whileTap="tap"
                                >
                                    {isSubmitting ? "Submitting..." : "Register"}
                                </motion.button>
                            </motion.div>
                        </form>
                    </motion.div>
                </div>
            </div>
            <OtpVerificationModal
                show={showOtpModal}
                mobile={formData.mobile}
                productType="Supply Chain Financing"
                onClose={() => setShowOtpModal(false)}
                onVerified={handleOtpSuccess}
            />
        </section>
    );
};

export default AnchorHeroSection;

